import { useState, useEffect, useCallback } from "react";
import {
  RefreshCw, FileText, Clock, CheckCircle, XCircle, Archive,
  Send, ExternalLink, User, Banknote, Inbox, Truck
} from "lucide-react";
import { toast } from "sonner";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const COLUMNS = [
  { key: "new", label: "Nouveau", icon: Send, color: "#3b82f6", bgCard: "border-blue-500/30 bg-blue-500/5", headerBg: "bg-blue-600/20 border-blue-500/40" },
  { key: "inprog", label: "En cours", icon: Clock, color: "#f59e0b", bgCard: "border-amber-500/30 bg-amber-500/5", headerBg: "bg-amber-600/20 border-amber-500/40" },
  { key: "pending_docs", label: "Attente Docs", icon: FileText, color: "#f97316", bgCard: "border-orange-500/30 bg-orange-500/5", headerBg: "bg-orange-600/20 border-orange-500/40" },
  { key: "sent_caisse", label: "Envoye Caisse", icon: Truck, color: "#6366f1", bgCard: "border-indigo-500/30 bg-indigo-500/5", headerBg: "bg-indigo-600/20 border-indigo-500/40" },
  { key: "done", label: "Cloture", icon: CheckCircle, color: "#10b981", bgCard: "border-emerald-500/30 bg-emerald-500/5", headerBg: "bg-emerald-600/20 border-emerald-500/40" },
  { key: "reject", label: "Refuse", icon: XCircle, color: "#ef4444", bgCard: "border-red-500/30 bg-red-500/5", headerBg: "bg-red-600/20 border-red-500/40" },
  { key: "paid", label: "Paye", icon: Banknote, color: "#059669", bgCard: "border-green-500/30 bg-green-500/5", headerBg: "bg-green-600/20 border-green-500/40" },
];

function DossierCard({ dossier, colConfig }) {
  const sentDate = dossier.sent_at
    ? new Date(dossier.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
    : "";

  return (
    <div
      className={`rounded-lg border p-3 ${colConfig.bgCard} transition-all hover:scale-[1.01]`}
      data-testid={`workflow-card-${dossier.row_number}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
            <User className="w-3.5 h-3.5 text-slate-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{dossier.nom} {dossier.prenom}</p>
            <p className="text-[11px] text-slate-500">{dossier.socialac_data?.ville || ""} {dossier.socialac_data?.code_postal || ""}</p>
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[10px] text-slate-600">{sentDate}</span>
        {dossier.socialac_data?.identite?.ville && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">
            {dossier.socialac_data.identite.ville}
          </span>
        )}
      </div>
    </div>
  );
}

export default function SocialACWorkflow() {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/socialac/dossiers`);
      const data = await res.json();
      setDossiers(data.dossiers || []);
    } catch (e) {
      console.error("Erreur chargement SocialAC:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await authFetch(`${API}/socialac/sync`, { method: "POST" });
      const data = await res.json();
      toast.success(`Synchro: ${data.updated || 0} mis a jour`);
      await loadData();
    } catch (e) {
      toast.error("Erreur de synchronisation");
    }
    setSyncing(false);
  };

  // Group by statut
  const grouped = {};
  COLUMNS.forEach(col => { grouped[col.key] = []; });
  dossiers.forEach(d => {
    const s = d.statut || "new";
    if (grouped[s]) grouped[s].push(d);
    else grouped.new.push(d);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-pink-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="socialac-workflow">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Workflow SocialAC</h2>
          <p className="text-sm text-slate-400">{dossiers.length} dossier{dossiers.length !== 1 ? "s" : ""} — Aides complementaires</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:text-white hover:border-slate-600 transition-colors disabled:opacity-50"
            data-testid="workflow-sync-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Synchro..." : "Sync"}
          </button>
          <a
            href={getApiUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-pink-600/20 border border-pink-500/30 rounded-lg text-sm text-pink-300 hover:bg-pink-600/30 transition-colors"
            data-testid="workflow-external-link"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Ouvrir SocialAC
          </a>
        </div>
      </div>

      {dossiers.length === 0 ? (
        <div className="text-center py-16">
          <Send className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Aucun dossier envoye a SocialAC</p>
          <p className="text-slate-600 text-xs mt-1">Envoyez des clients depuis le dashboard Aides Comp.</p>
        </div>
      ) : (
        /* Kanban board with 7 columns for v2 statuts */
        <div className="grid grid-cols-7 gap-3" data-testid="socialac-kanban">
          {COLUMNS.map(col => {
            const Icon = col.icon;
            const items = grouped[col.key] || [];
            return (
              <div key={col.key} className="flex flex-col" data-testid={`kanban-col-${col.key}`}>
                {/* Column header */}
                <div className={`rounded-t-xl border px-3 py-2.5 ${col.headerBg}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: col.color }} />
                      <span className="text-sm font-semibold text-white">{col.label}</span>
                    </div>
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${col.color}25`, color: col.color }}
                    >
                      {items.length}
                    </span>
                  </div>
                </div>
                {/* Cards */}
                <div className="flex-1 space-y-2 p-2 bg-slate-800/20 border border-t-0 border-slate-700/30 rounded-b-xl min-h-[200px]">
                  {items.length === 0 ? (
                    <p className="text-center text-xs text-slate-600 py-6">Aucun</p>
                  ) : (
                    items.map(d => (
                      <DossierCard key={d.socialac_id || d.row_number} dossier={d} colConfig={col} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
